import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import DataTable from '../../components/DataTable';
import ColumnConfig from '../../components/ColumnConfig';
import api from '../../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Calendar, Eye, Users, Clock, DollarSign, Star, MapPin, GraduationCap, CalendarDays, Copy, RefreshCw, MessageCircle } from 'lucide-react';
import { formatDateOnly } from '../../utils/timezone';

const dateOpts = { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' };

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Program sort order for dropdowns
const PROGRAM_SORT_ORDER = {
  'beginners': 1,
  'beginner': 1,
  'adv beginner': 2,
  'advanced beginner': 2,
  'u11': 3,
  'u13': 4,
  'u15': 5
};

const sortPrograms = (programs) => {
  return [...programs].sort((a, b) => {
    const aOrder = PROGRAM_SORT_ORDER[a.name?.toLowerCase()] || 100;
    const bOrder = PROGRAM_SORT_ORDER[b.name?.toLowerCase()] || 100;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.name.localeCompare(b.name);
  });
};

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Enrolled players dialog state
  const [enrolledPlayersDialog, setEnrolledPlayersDialog] = useState(false);
  const [enrolledPlayers, setEnrolledPlayers] = useState([]);
  const [loadingEnrolled, setLoadingEnrolled] = useState(false);
  const [selectedClassForEnrollment, setSelectedClassForEnrollment] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    program_id: '',
    coach_id: '',
    schedule_day: 'Monday',
    start_time: '09:00',
    end_time: '10:00',
    schedule_time: '',
    duration_weeks: '',
    sessions_per_week: 1,
    location: '',
    lane_id: '',
    price_per_session: '',
    max_capacity: 15,
    is_active: true,
    whatsapp_group_link: '',
    // Special program fields
    is_special_program: false,
    total_price: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    description: '',
    schedule_days: [],
    total_sessions: ''
  });

  const fetchData = async () => {
    try {
      const [classesRes, programsRes, coachesRes, lanesRes, locationsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/programs'),
        api.get('/coaches'),
        api.get('/lanes'),
        api.get('/locations/active').catch(() => ({ data: [] }))
      ]);
      setClasses(classesRes.data);
      setPrograms(sortPrograms(programsRes.data));
      setCoaches(coachesRes.data);
      setLanes(lanesRes.data || []);
      setLocations(locationsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEnrolledPlayers = async (classItem) => {
    setSelectedClassForEnrollment(classItem);
    setEnrolledPlayersDialog(true);
    setLoadingEnrolled(true);
    try {
      const response = await api.get(`/classes/${classItem.id}/enrollments`);
      setEnrolledPlayers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch enrolled players');
      setEnrolledPlayers([]);
    } finally {
      setLoadingEnrolled(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      program_id: '',
      coach_id: '',
      schedule_day: 'Monday',
      start_time: '09:00',
      end_time: '10:00',
      schedule_time: '',
      duration_weeks: '',
      sessions_per_week: 1,
      location: '',
      lane_id: '',
      price_per_session: '',
      max_capacity: 15,
      is_active: true,
      whatsapp_group_link: '',
      // Special program fields
      is_special_program: false,
      total_price: '',
      start_date: '',
      end_date: '',
      registration_deadline: '',
      description: '',
      schedule_days: [],
      total_sessions: ''
    });
    setEditingClass(null);
  };

  const handleEdit = (classItem) => {
    setEditingClass(classItem);
    setFormData({
      name: classItem.name,
      program_id: classItem.program_id,
      coach_id: classItem.coach_id || '',
      schedule_day: classItem.schedule_day,
      start_time: classItem.start_time || classItem.schedule_time?.split(' - ')[0] || '09:00',
      end_time: classItem.end_time || classItem.schedule_time?.split(' - ')[1] || '10:00',
      schedule_time: classItem.schedule_time || '',
      duration_weeks: classItem.duration_weeks || '',
      sessions_per_week: classItem.sessions_per_week || 1,
      location: classItem.location || '',
      lane_id: classItem.lane_id || '',
      price_per_session: classItem.price_per_session || '',
      max_capacity: classItem.max_capacity,
      is_active: classItem.is_active,
      whatsapp_group_link: classItem.whatsapp_group_link || '',
      // Special program fields
      is_special_program: classItem.is_special_program || false,
      total_price: classItem.total_price || '',
      start_date: classItem.start_date || '',
      end_date: classItem.end_date || '',
      registration_deadline: classItem.registration_deadline || '',
      description: classItem.description || '',
      schedule_days: classItem.schedule_days || [],
      total_sessions: classItem.total_sessions || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Format the data for submission
      const submitData = {
        ...formData,
        schedule_time: `${formData.start_time} - ${formData.end_time}`,
        duration_weeks: formData.duration_weeks ? parseInt(formData.duration_weeks) : null,
        sessions_per_week: parseInt(formData.sessions_per_week) || 1,
        price_per_session: formData.price_per_session ? parseFloat(formData.price_per_session) : null,
        lane_id: formData.lane_id || null,
        whatsapp_group_link: formData.whatsapp_group_link,  // Send as-is (empty string clears the field)
        // Special program fields
        total_price: formData.total_price ? parseFloat(formData.total_price) : null,
        total_sessions: formData.total_sessions ? parseInt(formData.total_sessions) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        registration_deadline: formData.registration_deadline || null,
        description: formData.description || null,
        schedule_days: formData.schedule_days?.length > 0 ? formData.schedule_days : null
      };
      
      if (editingClass) {
        await api.put(`/classes/${editingClass.id}`, submitData);
        toast.success('Class updated successfully');
      } else {
        await api.post('/classes', submitData);
        toast.success('Class created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const handleDelete = async (id) => {
    // Open the delete confirmation dialog
    const classItem = classes.find(c => c.id === id);
    setClassToDelete(classItem);
    setDeleteDialogOpen(true);
  };

  const handleCopy = (classItem) => {
    // Pre-fill the form with the class data, but clear the name to indicate it's a copy
    setFormData({
      name: `${classItem.name} (Copy)`,
      program_id: classItem.program_id || '',
      coach_id: classItem.coach_id || '',
      schedule_day: classItem.schedule_day || 'Monday',
      start_time: classItem.start_time || '09:00',
      end_time: classItem.end_time || '10:00',
      schedule_time: classItem.schedule_time || '',
      duration_weeks: classItem.duration_weeks || '',
      sessions_per_week: classItem.sessions_per_week || 1,
      location: classItem.location || '',
      lane_id: classItem.lane_id || '',
      price_per_session: classItem.price_per_session || '',
      max_capacity: classItem.max_capacity || 15,
      is_active: true,
      whatsapp_group_link: classItem.whatsapp_group_link || '',
      is_special_program: classItem.is_special_program || false,
      total_price: classItem.total_price || '',
      start_date: '', // Clear dates for copy
      end_date: '',
      registration_deadline: '',
      description: classItem.description || '',
      schedule_days: classItem.schedule_days || [],
      total_sessions: classItem.total_sessions || ''
    });
    setEditingClass(null); // This is a new class, not editing
    setDialogOpen(true);
    toast.info('Class details copied. Modify as needed and save.');
  };

  const confirmDelete = async () => {
    if (!classToDelete) return;
    try {
      await api.delete(`/classes/${classToDelete.id}`);
      toast.success('Class deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete class error:', error);
      const message = error.response?.data?.detail || 'Failed to delete class. Please try again.';
      toast.error(message);
    } finally {
      setDeleteDialogOpen(false);
      setClassToDelete(null);
    }
  };

  const viewClassDetails = (classItem) => {
    setSelectedClass(classItem);
    setDetailsDialog(true);
  };

  const columns = [
    {
      key: 'name',
      label: 'Class Name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900">{row.name}</p>
            {row.numeric_id && (
              <span className="text-xs text-slate-400 font-mono">ID: {row.numeric_id}</span>
            )}
            {row.is_special_program && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">
                <Star className="h-3 w-3 mr-1" />
                Special
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500">{row.location || 'No location'}</p>
        </div>
      )
    },
    {
      key: 'program_name',
      label: 'Program',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: programs.map(p => ({ value: p.name, label: p.name }))
    },
    {
      key: 'coach_name',
      label: 'Coach',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: coaches.map(c => ({ value: c.name, label: c.name }))
    },
    {
      key: 'schedule_day',
      label: 'Schedule',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: DAYS.map(d => ({ value: d, label: d })),
      render: (row) => (
        <div>
          <p className="font-medium">
            {row.schedule_days?.length > 0 
              ? row.schedule_days.join(', ') 
              : row.schedule_day}
          </p>
          <p className="text-sm text-slate-500">
            {row.start_time} - {row.end_time}
          </p>
          {row.is_special_program && row.start_date && (
            <p className="text-xs text-blue-600">
              {formatDateOnly(row.start_date, dateOpts)} - {row.end_date ? formatDateOnly(row.end_date, dateOpts) : 'TBD'}
            </p>
          )}
        </div>
      )
    },
    {
      key: 'pricing',
      label: 'Pricing',
      sortable: false,
      render: (row) => (
        <div className="text-sm">
          {row.is_special_program && row.total_price ? (
            <p className="font-medium text-green-600">{cs()}{row.total_price.toFixed(2)} total</p>
          ) : row.price_per_session ? (
            <p className="font-medium">{cs()}{row.price_per_session.toFixed(2)}/session</p>
          ) : (
            <span className="text-slate-400">Program rate</span>
          )}
          {row.duration_weeks && (
            <p className="text-xs text-slate-500">{row.duration_weeks} weeks</p>
          )}
        </div>
      )
    },
    {
      key: 'current_enrollment',
      label: 'Enrollment',
      sortable: true,
      render: (row) => (
        <button
          onClick={() => fetchEnrolledPlayers(row)}
          className={`font-medium hover:underline cursor-pointer ${
            row.current_enrollment >= row.max_capacity 
              ? 'text-rose-600' 
              : 'text-blue-600'
          }`}
          data-testid={`view-enrolled-${row.id}`}
        >
          {row.current_enrollment}/{row.max_capacity}
        </button>
      )
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: locations.map(l => ({ value: l.name, label: l.name })),
      render: (row) => (
        <span className="text-sm text-slate-600">
          {row.location || '-'}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      render: (row) => (
        <Badge className={row.is_active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      )
    }
  ];

  const actions = (row) => (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="outline"
        size="icon"
        onClick={() => viewClassDetails(row)}
        data-testid={`view-class-${row.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleCopy(row)}
        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        title="Copy Class"
        data-testid={`copy-class-${row.id}`}
      >
        <Copy className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleEdit(row)}
        data-testid={`edit-class-${row.id}`}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleDelete(row.id)}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        data-testid={`delete-class-${row.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title="Classes">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Classes">
      <div className="space-y-6" data-testid="classes-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage class schedules and special programs</p>
          <div className="flex flex-wrap items-center gap-2">
            <ColumnConfig
              columns={columns}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="classes-columns"
              requiredColumns={['name']}
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-class-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Class
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  {formData.is_special_program ? (
                    <Star className="h-5 w-5 text-amber-500" />
                  ) : (
                    <Calendar className="h-5 w-5 text-blue-500" />
                  )}
                  {editingClass ? 'Edit Class' : 'Create New Class'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Special Program Toggle */}
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-500" />
                    <div>
                      <Label className="font-medium">Special Program / Camp</Label>
                      <p className="text-xs text-slate-500">Enable for camps, clinics, or limited-time programs</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.is_special_program}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_special_program: checked })}
                    data-testid="class-special-program-switch"
                  />
                </div>

                {/* Basic Info */}
                <div className="space-y-2">
                  <Label>Class / Program Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={formData.is_special_program ? "e.g., Summer Batting Camp 2026" : "e.g., Morning Batting Practice"}
                    required
                    data-testid="class-name-input"
                  />
                </div>

                {formData.is_special_program && (
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe this special program..."
                      rows={2}
                      data-testid="class-description-input"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Program *</Label>
                    <Select
                      value={formData.program_id}
                      onValueChange={(value) => setFormData({ ...formData, program_id: value })}
                    >
                      <SelectTrigger data-testid="class-program-select">
                        <SelectValue placeholder="Select program" />
                      </SelectTrigger>
                      <SelectContent>
                        {programs.map((program) => (
                          <SelectItem key={program.id} value={program.id}>
                            {program.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <GraduationCap className="h-4 w-4" /> Coach
                    </Label>
                    <Select
                      value={formData.coach_id}
                      onValueChange={(value) => setFormData({ ...formData, coach_id: value })}
                    >
                      <SelectTrigger data-testid="class-coach-select">
                        <SelectValue placeholder="Select coach" />
                      </SelectTrigger>
                      <SelectContent>
                        {coaches.map((coach) => (
                          <SelectItem key={coach.id} value={coach.id}>
                            {coach.display_name || coach.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Special Program Dates */}
                {formData.is_special_program && (
                  <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-blue-700 flex items-center gap-1">
                      <CalendarDays className="h-4 w-4" /> Program Dates
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm">Start Date *</Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                          data-testid="class-start-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">End Date *</Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                          data-testid="class-end-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Registration Deadline</Label>
                        <Input
                          type="date"
                          value={formData.registration_deadline}
                          onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                          data-testid="class-registration-deadline-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Schedule Day - Different for special programs */}
                {formData.is_special_program ? (
                  <div className="space-y-2">
                    <Label>Schedule Days (select multiple)</Label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            const newDays = formData.schedule_days?.includes(day)
                              ? formData.schedule_days.filter(d => d !== day)
                              : [...(formData.schedule_days || []), day];
                            setFormData({ ...formData, schedule_days: newDays, schedule_day: newDays[0] || 'Monday' });
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            formData.schedule_days?.includes(day)
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`class-day-${day.toLowerCase()}`}
                        >
                          {day.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Schedule Day</Label>
                    <Select
                      value={formData.schedule_day}
                      onValueChange={(value) => setFormData({ ...formData, schedule_day: value })}
                    >
                      <SelectTrigger data-testid="class-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS.map((day) => (
                          <SelectItem key={day} value={day}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Clock className="h-4 w-4" /> Start Time *
                    </Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      required
                      data-testid="class-start-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time *</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      required
                      data-testid="class-end-time-input"
                    />
                  </div>
                </div>

                {/* Location and Lane */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> Location
                    </Label>
                    <Select
                      value={formData.location || "none"}
                      onValueChange={(value) => setFormData({ ...formData, location: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="class-location-input">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select location</SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.name}>
                            {loc.name}
                            {loc.is_primary && <span className="text-xs text-blue-500 ml-1">(Primary)</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assigned Lane</Label>
                    <Select
                      value={formData.lane_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, lane_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="class-lane-select">
                        <SelectValue placeholder="Select lane (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No lane</SelectItem>
                        {lanes.map((lane) => (
                          <SelectItem key={lane.id} value={lane.id}>
                            {lane.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Duration and Frequency */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Duration (weeks)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.duration_weeks}
                      onChange={(e) => setFormData({ ...formData, duration_weeks: e.target.value })}
                      placeholder="e.g., 12"
                      data-testid="class-duration-weeks-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sessions/Week</Label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={formData.sessions_per_week}
                      onChange={(e) => setFormData({ ...formData, sessions_per_week: e.target.value })}
                      data-testid="class-sessions-per-week-input"
                    />
                  </div>
                  {formData.is_special_program && (
                    <div className="space-y-2">
                      <Label>Total Sessions</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.total_sessions}
                        onChange={(e) => setFormData({ ...formData, total_sessions: e.target.value })}
                        placeholder="e.g., 10"
                        data-testid="class-total-sessions-input"
                      />
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div className="p-3 bg-green-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                    <DollarSign className="h-4 w-4" /> Pricing
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {formData.is_special_program && (
                      <div className="space-y-2">
                        <Label className="text-sm">Total Program Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.total_price}
                          onChange={(e) => setFormData({ ...formData, total_price: e.target.value })}
                          placeholder="e.g., 299.00"
                          data-testid="class-total-price-input"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-sm">Price Per Session ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price_per_session}
                        onChange={(e) => setFormData({ ...formData, price_per_session: e.target.value })}
                        placeholder="Optional override"
                        data-testid="class-price-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Capacity and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Users className="h-4 w-4" /> Max Capacity *
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_capacity}
                      onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) })}
                      required
                      data-testid="class-capacity-input"
                    />
                  </div>
                  <div className="flex items-center justify-between pt-7">
                    <Label>Active Class</Label>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      data-testid="class-active-switch"
                    />
                  </div>
                </div>

                {/* WhatsApp Group Link */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-green-600" /> WhatsApp Group Link
                  </Label>
                  <Input
                    type="url"
                    placeholder="https://chat.whatsapp.com/..."
                    value={formData.whatsapp_group_link}
                    onChange={(e) => setFormData({ ...formData, whatsapp_group_link: e.target.value })}
                    data-testid="class-whatsapp-input"
                  />
                  <p className="text-xs text-slate-500">
                    This link will be included in enrollment confirmation emails so parents can join the class group.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="save-class-btn">
                    {editingClass ? 'Update Class' : 'Create Class'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Tabs for All/Regular/Special */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">
              All Classes ({classes.length})
            </TabsTrigger>
            <TabsTrigger value="regular" data-testid="tab-regular">
              Regular ({classes.filter(c => !c.is_special_program).length})
            </TabsTrigger>
            <TabsTrigger value="special" data-testid="tab-special">
              <Star className="h-4 w-4 mr-1" />
              Special Programs ({classes.filter(c => c.is_special_program).length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <DataTable
              data={activeTab === 'all' 
                ? classes 
                : activeTab === 'special' 
                  ? classes.filter(c => c.is_special_program) 
                  : classes.filter(c => !c.is_special_program)}
              columns={columns}
              visibleColumns={visibleColumns}
              actions={actions}
              emptyIcon={activeTab === 'special' ? Star : Calendar}
              emptyMessage={activeTab === 'special' 
                ? "No special programs. Create a camp or clinic!" 
                : "No classes scheduled. Create your first class!"}
              searchable={true}
              searchFields={['name', 'program_name', 'coach_name', 'location', 'schedule_day']}
              testIdPrefix="classes"
            />
          </CardContent>
        </Card>

        {/* Class Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                {selectedClass?.is_special_program ? (
                  <Star className="h-5 w-5 text-amber-500" />
                ) : (
                  <Calendar className="h-5 w-5 text-blue-500" />
                )}
                {selectedClass?.is_special_program ? 'Special Program Details' : 'Class Details'}
              </DialogTitle>
            </DialogHeader>
            
            {selectedClass && (
              <div className="space-y-4 mt-4">
                <div className={`rounded-lg p-4 ${
                  selectedClass.is_special_program 
                    ? 'bg-gradient-to-r from-amber-50 to-orange-50' 
                    : 'bg-gradient-to-r from-blue-50 to-slate-50'
                }`}>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-lg font-bold text-slate-900">
                      {selectedClass.name}
                    </h3>
                    {selectedClass.is_special_program && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Star className="h-3 w-3 mr-1" />
                        Special
                      </Badge>
                    )}
                  </div>
                  <Badge className={selectedClass.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                    {selectedClass.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  {selectedClass.description && (
                    <p className="text-sm text-slate-600 mt-2">{selectedClass.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Program</p>
                    <p className="font-medium">{selectedClass.program_name || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <GraduationCap className="h-3 w-3" /> Coach
                    </p>
                    <p className="font-medium">{selectedClass.coach_name || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Schedule
                    </p>
                    <p className="font-medium">
                      {selectedClass.schedule_days?.length > 0 
                        ? selectedClass.schedule_days.join(', ') 
                        : selectedClass.schedule_day}
                    </p>
                    <p className="text-sm">{selectedClass.start_time} - {selectedClass.end_time}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Users className="h-3 w-3" /> Enrollment
                    </p>
                    <p className={`font-medium ${
                      selectedClass.current_enrollment >= selectedClass.max_capacity 
                        ? 'text-rose-600' 
                        : 'text-green-600'
                    }`}>
                      {selectedClass.current_enrollment}/{selectedClass.max_capacity}
                    </p>
                  </div>
                </div>

                {/* Special Program Specific Info */}
                {selectedClass.is_special_program && (
                  <div className="p-3 bg-amber-50 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-1">
                      <CalendarDays className="h-4 w-4" /> Program Dates
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {selectedClass.start_date && (
                        <div>
                          <span className="text-slate-500">Start:</span>{' '}
                          <span className="font-medium">{formatDateOnly(selectedClass.start_date, dateOpts)}</span>
                        </div>
                      )}
                      {selectedClass.end_date && (
                        <div>
                          <span className="text-slate-500">End:</span>{' '}
                          <span className="font-medium">{formatDateOnly(selectedClass.end_date, dateOpts)}</span>
                        </div>
                      )}
                      {selectedClass.registration_deadline && (
                        <div className="col-span-2">
                          <span className="text-slate-500">Registration Deadline:</span>{' '}
                          <span className="font-medium text-rose-600">
                            {formatDateOnly(selectedClass.registration_deadline, dateOpts)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pricing Info */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" /> Pricing
                  </p>
                  {selectedClass.is_special_program && selectedClass.total_price ? (
                    <p className="font-medium text-green-600">{cs()}{selectedClass.total_price.toFixed(2)} total</p>
                  ) : selectedClass.price_per_session ? (
                    <p className="font-medium">{cs()}{selectedClass.price_per_session.toFixed(2)}/session</p>
                  ) : (
                    <p className="text-slate-500">Uses program rate</p>
                  )}
                  {selectedClass.duration_weeks && (
                    <p className="text-sm text-slate-500">{selectedClass.duration_weeks} weeks, {selectedClass.sessions_per_week} session(s)/week</p>
                  )}
                  {selectedClass.total_sessions && (
                    <p className="text-sm text-slate-500">{selectedClass.total_sessions} total sessions</p>
                  )}
                </div>

                {selectedClass.location && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Location
                    </p>
                    <p className="font-medium">{selectedClass.location}</p>
                    {selectedClass.lane_name && (
                      <p className="text-sm text-slate-500">Lane: {selectedClass.lane_name}</p>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setDetailsDialog(false)}>
                    Close
                  </Button>
                  <Button onClick={() => { setDetailsDialog(false); handleEdit(selectedClass); }}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit Class
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl text-rose-600">
                Delete Class
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>"{classToDelete?.name}"</strong>? 
                This action cannot be undone.
                {classToDelete?.current_enrollment > 0 && (
                  <span className="block mt-2 text-rose-500 font-medium">
                    Warning: This class has {classToDelete.current_enrollment} enrolled player(s). 
                    You must remove all enrollments before deleting.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-class-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDelete}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="delete-class-confirm"
              >
                Delete Class
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Enrolled Players Dialog */}
        <Dialog open={enrolledPlayersDialog} onOpenChange={setEnrolledPlayersDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Users className="h-5 w-5" />
                Enrolled Players - {selectedClassForEnrollment?.name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingEnrolled ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : enrolledPlayers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No players enrolled in this class</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 mb-4">
                  {enrolledPlayers.length} player(s) enrolled
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Player</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrolledPlayers.map((enrollment, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <span className="font-medium">{enrollment.player_name}</span>
                        </TableCell>
                        <TableCell>{enrollment.parent_name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {enrollment.parent_email && (
                              <p className="text-slate-600">{enrollment.parent_email}</p>
                            )}
                            {enrollment.parent_phone && (
                              <p className="text-slate-500">{enrollment.parent_phone}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            enrollment.enrollment_status === 'active' || enrollment.enrollment_status === 'current'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }>
                            {enrollment.enrollment_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Classes;
